import { ILogbookSession, ILogbookSessionType, ILogbookSyncStatus } from "../Types"
import { Client } from "./Client"
import { localStorageId, localStorageValue } from "./LocalStorage"

/**
 * Non-React session/storage orchestration extracted from `App.tsx`. Mirrors the
 * `AppData` pattern: this module owns the localStorage + socket sequencing so it
 * can be unit-tested in isolation, while the `app.*` methods stay thin wrappers
 * that only wire React state setters.
 *
 * The persisted keys (`logbook-session`, `logbook-ship`,
 * `logbook-session-restore`, `logbook-session-return`) and the `1000`/`10000`
 * socket-stream cadence are OBSERVABLE behaviour and are kept exactly as they
 * were in `App.tsx`.
 */

// What the React layer must do after a logout has cleared its state. Window
// navigation is intentionally left to the caller (jsdom forbids
// `location.reload`/assignment), keeping this decision testable.
export type LogoutRedirect =
  | { action: "reload" }
  | { action: "redirect"; url: string }
  | { action: "none" };

// What the app should do when `session.get` fails outright, as opposed to
// answering 200 with `authorized: false` (a genuinely expired session, handled
// in the load effect's success branch).
export type SessionLoadFailure = "logout" | "retry";
export type ShipLoadFailure = "retry" | "giveUp";

export const Session = {
  isLoopbackHost(host: string): boolean {
    return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  },
  isCompanionRoute(path: string): boolean {
    return /^\/app\/ships\/\d+\/companion\/[^/]+\/?$/.test(path);
  },
  localReturnURL(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    if (value === "logbook://app/account") return value;
    try {
      const url = new URL(value);
      if ((url.protocol === "http:" || url.protocol === "https:")
        && this.isLoopbackHost(url.hostname) && !url.username && !url.password
        && url.pathname === "/app/account" && !url.search && !url.hash) {
        return url.href;
      }
    } catch {
      // Untrusted navigation input is not a usable return destination.
    }
    return undefined;
  },
  beginCompanion(shipId: number): void {
    localStorage.setItem("logbook-companion", shipId.toString());
  },
  clearCompanion(shipId: number): void {
    if (localStorageId("logbook-companion") === shipId) {
      localStorage.removeItem("logbook-companion");
      if (localStorageId("logbook-ship") === shipId) {
        localStorage.removeItem("logbook-ship");
      }
    }
  },
  streamInterval(isPrimary: boolean): number {
    return isPrimary ? 1000 : 10000;
  },
  // Whether a failed session load is a definitive answer ("this session is over")
  // or something to come back from. ONLY an auth rejection ends the session:
  // `GET /api/session` (`Sources/LogbookApp/Session.swift`) answers 401 for a
  // token it doesn't know, and everything else it can emit is a state the app
  // can still recover from -- notably the 503 it returns while the account row
  // it needs hasn't been synced into the local DB yet.
  //
  // Previously *every* 4xx logged the user out, which meant a 404 (which that
  // route used to return for exactly that not-ready-yet case) silently dropped a
  // perfectly valid stored token and dumped the user back on the login screen
  // (#1300).
  // Whether a failed ship load is worth another attempt (#1279). Selecting a ship
  // runs `newInstance(shipId:reset: true)` -> `native.resetDB()`, which deletes and
  // re-downloads the local ship DB; requests landing in that window cannot be
  // answered yet. The app reports that as 503 (and, if the schema has not been
  // recreated yet, a request can still surface a raw DB error as a 500), while the
  // pre-#1279 build answered 401 for the same race -- so all of those retry.
  //
  // A NETWORK-level failure retries too, and this is the common case rather than the
  // exotic one: selecting a ship RESTARTS the app instance (`POST /api/ship` ->
  // "start instance" in `LogbookApp.swift`), which drops every in-flight connection.
  // WebKit reports that as `TypeError: Load failed` -- an error with no `httpStatus`
  // at all -- so treating "no status" as unrecognisable gave up instantly on a
  // request that failed only because the server was mid-restart, one it would have
  // answered a second later. Captured from a real run: three instance restarts in
  // 30s, and the app never recovered from the first dropped request (#1279).
  //
  // A 404 retries too, for the same reason the session load already does (#1300).
  // The readiness gate answers 503 while `syncStatus` is `.selectShip` or
  // `.initialSyncInProgress`, but that clears when the SYNC finishes -- and the
  // `ships` row is a separate row that can still be absent for a moment afterwards,
  // so a request landing in that sliver gets an ordinary "no such ship". Captured on
  // one instance four milliseconds apart: `GET /api/ships/6` -> serviceUnavailable,
  // then `GET /api/ships/6` -> notFound. Giving up on the second stranded the app on
  // the account route with the ship never opening.
  //
  // A 403 still gives up: that is an answer about permission, which re-asking cannot
  // change. So does a status present but not a number -- a malformed response rather
  // than a transient one. Retrying is bounded (5 attempts, ~15s) and non-destructive,
  // so a genuinely missing ship still ends up at the ship list, just a few seconds
  // later, while a ship that was moments from being ready now opens.
  classifyShipLoadFailure(error: unknown): ShipLoadFailure {
    const status = (error as { httpStatus?: unknown } | undefined)?.httpStatus;
    if (status === undefined) {
      return "retry";
    }
    if (status === 401 || status === 404) {
      return "retry";
    }
    return typeof status == "number" && status >= 500 ? "retry" : "giveUp";
  },

  classifySessionLoadFailure(error: unknown): SessionLoadFailure {
    const status = (error as { httpStatus?: unknown } | undefined)?.httpStatus;
    return status === 401 || status === 403 ? "logout" : "retry";
  },
  // Whether the app's own sync status says the local ship DB is still being
  // (re)populated, so a retryable ship-load failure means "not yet" rather than
  // "not working". These are exactly the two statuses
  // `LogbookApp.localDataUnavailable()` answers 503 for.
  //
  // The bounded ship-load budget above (5 attempts, ~15s) was sized for the short
  // races AROUND a ship select, not for the initial sync itself: that downloads a
  // whole ship and routinely runs for minutes, with every request answered 503 for
  // its entire duration. Spending the budget on it meant selecting a ship gave up
  // and dropped the user on `/app/account` while the sync went on to finish
  // normally. An attempt made in this window is therefore excused rather than
  // spent -- the budget stays intact for once the sync is over.
  //
  // `initialSyncFailed` deliberately does NOT wait: it is a real, settled outcome
  // that must surface at the ship list where the user can act on it, not a state
  // to sit on indefinitely. Nor does an unreadable status (the app was mid-restart),
  // which is precisely what the bounded budget is for.
  shipLoadWaitsForSync(status?: ILogbookSyncStatus): boolean {
    return status == ILogbookSyncStatus.selectShip || status == ILogbookSyncStatus.initialSyncInProgress;
  },
  // How long before the next ship-load attempt. `undefined` means the attempt is
  // waiting on the sync (see above): those are unbounded, so they poll at a steady
  // cadence instead of doubling away into being minutes late noticing a sync that
  // has long finished. Budgeted attempts keep the escalating backoff.
  shipLoadRetryDelay(attempt?: number): number {
    return attempt == undefined ? 3000 : 1000 * Math.pow(2, attempt);
  },
  // The app's launch route (`/app/launch`) is the only place a wiped
  // localStorage session is restored from the server. The native clients always
  // load it over loopback (localhost), and the backend independently hard-gates
  // restore to the loopback peer, so this is a UX gate, not the security
  // boundary. Matches `/app/launch` (and a bare `/launch`) with any trailing
  // slash, but NOT the `/app/ships/:id/launch` (launchShip) route.
  isLaunchRoute(pathname: string): boolean {
    const path = pathname.replace(/\/+$/, "");
    return path == "/launch" || path.endsWith("/app/launch");
  },
  // Whether to attempt a server-side session restore: only on the launch route,
  // only for a device app session (never a cloud session), and only when the
  // localStorage token is actually missing (a present token loads normally).
  shouldRestore(pathname: string, cloudSession: boolean): boolean {
    return !cloudSession && !localStorageValue("logbook-session") && this.isLaunchRoute(pathname);
  },
  clearSession(): void {
    localStorage.removeItem("logbook-session");
    localStorage.removeItem("logbook-ship");
    // A companion device's pairing (`ShipSelectView.tsx`'s `connectCompanion`)
    // sets this key on connect but nothing ever cleared it again -- a companion
    // that logged out and was later relaunched (`AppView.tsx`'s `/launch`-route
    // effect, the native app's boot path) would silently snap back into the
    // stale companion ship. See `Tests/E2E/companion-disconnect.spec.ts`.
    localStorage.removeItem("logbook-companion");
  },
  resolveLogoutRedirect(isLocalSession: boolean | undefined): LogoutRedirect {
    const restoreId = localStorageValue("logbook-session-restore");
    if (restoreId) {
      localStorage.removeItem("logbook-session-restore");
      localStorage.setItem("logbook-session", restoreId);
      return { action: "reload" };
    }
    // A revoked token can fail on initial load, before its session type is known.
    if (isLocalSession !== false) {
      const returnUrl = this.localReturnURL(localStorageValue("logbook-session-return"));
      if (returnUrl) {
        // `logbook-companion`/`logbook-ship` were set on the COMPANION
        // device's own origin (`ShipSelectView.tsx`/`OverlayContentView.tsx`'s
        // pairing flow), but this logout is running on the PRIMARY's origin
        // (the companion browser is a thin client of the primary while
        // paired -- see `Tests/E2E/companion-disconnect.spec.ts`), so
        // `clearSession()` above cannot reach those keys: they live in a
        // different origin's localStorage. Tag the cross-origin redirect so
        // the companion's own origin can clear its own stale keys once it
        // lands there (see `AppView.tsx`'s handling of this param) --
        // otherwise a later native relaunch would silently try to re-pair
        // with a connection that was just explicitly disconnected
        // (`logbook-companion`), and the stale `logbook-ship`/`app.shipId`
        // would leave the "current ship" block and `ShipSelectView`'s
        // `alreadyOpened` row both rendering for the same ship (#1098).
        return { action: "redirect", url: `${returnUrl}?companion_disconnected=1` };
      }
    }
    return { action: "none" };
  },
  // Persists a newly authorized session. Takes the session's OWN type rather than
  // a pre-computed boolean: `App.tsx` used to pass its current `sessionType` React
  // state here, which is `undefined` until the first login resolves, so the very
  // first login after any app start was persisted as non-primary -- removing
  // `logbook-ship`, returning no shipId, and running the socket at the companion's
  // 10s cadence until the session reload repaired it. Deriving it here removes the
  // chance of passing the app's *previous* type by mistake (#1279).
  persistSession(socket: Client, session: ILogbookSession, type: ILogbookSessionType | undefined): { shipId: number | undefined } {
    const isPrimary = type == ILogbookSessionType.primary;
    localStorage.removeItem("logbook-ship");
    if (session.id) {
      localStorage.setItem("logbook-session", session.id);
      socket.session = session.id;
    } else {
      socket.session = undefined;
    }
    let shipId: number | undefined = undefined;
    if (isPrimary) {
      shipId = session.shipId;
      if (session.shipId) {
        localStorage.setItem("logbook-ship", session.shipId.toString());
      }
    }
    socket.stream(this.streamInterval(isPrimary));
    return { shipId };
  },
  persistShipId(shipId: number, close?: boolean): void {
    if (close) {
      localStorage.removeItem("logbook-ship");
    } else {
      localStorage.setItem("logbook-ship", shipId.toString());
    }
  },
};
